import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import moment from "moment";
import { useSelector } from "react-redux";

import {
  Col,
  DatePicker,
  Input,
  notification,
  Row,
  Select,
  Spin,
  Table,
} from "antd";
import ButtonStandard from "../../../components/utils/button";
import { Container, Footer } from "./styles";
import { getColumnSearchProps } from "../../../utils/tableUtils";

import { ANIMAL_HANDLING_ALERT_FORM_INITIAL_STATE } from "../../../contexts/animalHandlingAlertContext";
import useAnimalHandlingAlertContext from "../../../hooks/useAnimalHandlingAlertContext";
import { save } from "../../../services/animalHandlingAlertService";
import { callAnimalIndex } from "../../../services/animalService";

const SORT_FIELD_MAP = {
  handlingNumber: "handling_number",
  lotName: "lot_name",
  breedName: "breed_name",
};

function buildSearchString(filters) {
  let parts = ["farm_status='A'"];
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || (Array.isArray(value) && value.length === 0)) return;
      const field = SORT_FIELD_MAP[key] || key;
      const str = Array.isArray(value) ? value[0] : value;
      parts.push(`upper(${field}) like upper('%25${str}%25')`);
    });
  }
  return parts.join(";");
}

const { Option } = Select;

const validationSchema = Yup.object({
  message: Yup.string().trim().required(),
  visualizationType: Yup.string()
    .oneOf(["PERMANENT", "CONFIRM", "EXPIRATION"])
    .required(),
  expirationDate: Yup.mixed().when("visualizationType", {
    is: "EXPIRATION",
    then: Yup.mixed().required(),
    otherwise: Yup.mixed().nullable(),
  }),
});

const AnimalHandlingAlertForm = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { data, isDrawerVisible, closeDrawer, fetchData } =
    useAnimalHandlingAlertContext();

  const formikRef = useRef();
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);
  const [animalList, setAnimalList] = useState([]);
  const [isLoadingAnimals, setIsLoadingAnimals] = useState(false);
  const [animalsSelectedKeys, setAnimalsSelectedKeys] = useState([]);
  const [animalTotal, setAnimalTotal] = useState(0);
  const [tableState, setTableState] = useState({
    page: 1,
    size: 10,
    sorter: null,
    filters: {},
  });

  const fetchAnimals = useCallback(
    async (page, size, sorter, filters) => {
      setIsLoadingAnimals(true);
      try {
        const sortField = sorter?.columnKey
          ? SORT_FIELD_MAP[sorter.columnKey] || sorter.columnKey
          : null;
        const sorterPayload =
          sortField && sorter?.order
            ? { field: sortField, order: sorter.order }
            : null;

        const {
          data: { results },
        } = await callAnimalIndex({
          groupId,
          farmId,
          page,
          size,
          sorter: sorterPayload,
          filters: buildSearchString(filters),
        });
        const content = results?.content ?? results ?? [];
        setAnimalList(content);
        setAnimalTotal(results?.totalElements ?? content.length);
      } catch (error) {
        setAnimalList([]);
      } finally {
        setIsLoadingAnimals(false);
      }
    },
    [farmId, groupId],
  );

  useEffect(() => {
    if (isDrawerVisible) {
      fetchAnimals(
        tableState.page,
        tableState.size,
        tableState.sorter,
        tableState.filters,
      );
    }
  }, [isDrawerVisible, tableState, fetchAnimals]);

  useEffect(() => {
    if (isDrawerVisible && formikRef.current) {
      formikRef.current.resetForm({
        values: data
          ? { ...data, animalIds: [] }
          : { ...ANIMAL_HANDLING_ALERT_FORM_INITIAL_STATE },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerVisible]);

  const handleAnimalTableChange = useCallback((pagination, filters, sorter) => {
    setTableState({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sorter?.columnKey ? sorter : null,
      filters: filters || {},
    });
  }, []);

  const handleAfterVisibleChange = useCallback((visible) => {
    if (visible) {
      setTableState({ page: 1, size: 10, sorter: null, filters: {} });
    }
  }, []);

  const handleClose = useCallback(() => {
    if (formikRef.current) {
      formikRef.current.resetForm(ANIMAL_HANDLING_ALERT_FORM_INITIAL_STATE);
    }
    setTableState({ page: 1, size: 10, sorter: null, filters: {} });
    setAnimalsSelectedKeys([]);
    closeDrawer();
  }, [closeDrawer, formikRef]);

  const handleSubmit = useCallback(
    async (values) => {
      setIsLoadingRequest(true);
      if (animalsSelectedKeys.length === 0) {
        notification.error({
          message:
            translation.animalHandlingAlert.form.notifications
              .validationAnimals,
        });
        setIsLoadingRequest(false);
        return;
      }
      try {
        const body = {
          animalIds: animalsSelectedKeys,
          message: values.message,
          visualizationType: values.visualizationType,
          expirationDate:
            values.visualizationType === "EXPIRATION"
              ? moment(values.expirationDate).format("YYYY-MM-DD")
              : null,
          status: values.status || "A",
        };
        await save({ groupId, farmId, id: values.id, body });
        notification.success({
          message:
            translation.animalHandlingAlert.form.notifications.saveSuccess,
        });
        fetchData();
        handleClose();
      } catch (error) {
        notification.error({
          message: translation.animalHandlingAlert.form.notifications.saveError,
        });
      } finally {
        setIsLoadingRequest(false);
      }
    },
    [
      animalsSelectedKeys,
      farmId,
      fetchData,
      groupId,
      handleClose,
      translation.animalHandlingAlert.form.notifications.saveError,
      translation.animalHandlingAlert.form.notifications.saveSuccess,
      translation.animalHandlingAlert.form.notifications.validationAnimals,
    ],
  );

  const drawerTitle = data
    ? translation.animalHandlingAlert.form.titleEdit
    : translation.animalHandlingAlert.form.titleNew;

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const animalColumns = [
    {
      title: translation.animal?.handlingNumber || "Nº de Manejo",
      width: 180,
      dataIndex: "handlingNumber",
      key: "handlingNumber",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "handlingNumber"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("handlingNumber", handleReset, handleSearch),
    },
    {
      title: translation.animal?.tagId || "Id. Eletrônica",
      width: 180,
      dataIndex: "tagId",
      key: "tagId",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "tagId"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("tagId", handleReset, handleSearch),
    },
    {
      title: translation.animal?.sisbov || "SISBOV",
      width: 120,
      dataIndex: "sisbov",
      key: "sisbov",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "sisbov"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("sisbov", handleReset, handleSearch),
      render: (text) => text || "",
    },
    {
      title: translation.animal?.nickname || "Nome",
      width: 120,
      dataIndex: "nickname",
      key: "nickname",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "nickname"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("nickname", handleReset, handleSearch),
    },
    {
      title: translation.animal?.gender || "Sexo",
      width: 120,
      dataIndex: "gender",
      key: "gender",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "gender"
          ? tableState.sorter.order
          : null,
      filterMultiple: false,
      filters: [
        { text: translation.animalGender.Male || "Macho", value: "M" },
        { text: translation.animalGender.Female || "Fêmea", value: "F" },
      ],
    },
    {
      title: translation.animal?.lotName || "Lote",
      width: 180,
      dataIndex: "lotName",
      key: "lotName",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "lotName"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("lotName", handleReset, handleSearch),
    },
    {
      title: translation.animal?.breed || "Raça",
      width: 120,
      dataIndex: "breedName",
      key: "breedName",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "breedName"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("breedName", handleReset, handleSearch),
    },
  ];

  return (
    <Container
      title={drawerTitle}
      width={720}
      onClose={handleClose}
      maskClosable={false}
      visible={isDrawerVisible}
      afterVisibleChange={handleAfterVisibleChange}
    >
      <Formik
        ref={formikRef}
        enableReinitialize={false}
        initialValues={
          data ? { ...data } : { ...ANIMAL_HANDLING_ALERT_FORM_INITIAL_STATE }
        }
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => {
          const {
            values,
            errors,
            submitCount,
            setValues,
            handleSubmit: formikSubmit,
          } = formikProps;

          const rowSelection = {
            selectedRowKeys: animalsSelectedKeys,
            onChange: (selectedKeys) => {
              setAnimalsSelectedKeys(selectedKeys);
            },
          };

          return (
            <Spin spinning={isLoadingRequest}>
              <form onSubmit={formikSubmit} autoComplete="off">
                <div className="drawerForm">
                  {Object.entries(errors).length > 0 && submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )}
                  {/* Animal selection table */}
                  <Row type="flex" style={{ marginBottom: 8 }}>
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label
                          className={
                            errors.animalIds && submitCount > 0 ? "error" : ""
                          }
                        >
                          {translation.animalHandlingAlert.form.animals} *
                        </label>
                      </Row>
                      <Row>
                        <Table
                          rowKey="id"
                          size="small"
                          loading={isLoadingAnimals}
                          dataSource={animalList}
                          rowSelection={rowSelection}
                          columns={animalColumns}
                          onChange={handleAnimalTableChange}
                          pagination={{
                            current: tableState.page,
                            pageSize: tableState.size,
                            total: animalTotal,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                          }}
                          scroll={{ y: 240, x: true }}
                        />
                      </Row>
                    </Col>
                  </Row>

                  {/* Message */}
                  <Row type="flex" style={{ marginTop: 13 }} align="middle">
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label
                          className={
                            errors.message && submitCount > 0 ? "error" : ""
                          }
                        >
                          {translation.animalHandlingAlert.form.message} *
                        </label>
                      </Row>
                      <Row>
                        <Input.TextArea
                          value={values.message || ""}
                          autoSize={{ minRows: 3 }}
                          onChange={(e) =>
                            setValues({ ...values, message: e.target.value })
                          }
                        />
                      </Row>
                    </Col>
                  </Row>

                  {/* Visualization Type */}
                  <Row type="flex" style={{ marginTop: 13 }} align="middle">
                    <Col span={24}>
                      <Row className="rowLabel">
                        <label
                          className={
                            errors.visualizationType && submitCount > 0
                              ? "error"
                              : ""
                          }
                        >
                          {
                            translation.animalHandlingAlert.form
                              .visualizationType.label
                          }{" "}
                          *
                        </label>
                      </Row>
                      <Row>
                        <Select
                          value={values.visualizationType || undefined}
                          placeholder={translation.defaultSelectPlaceholder}
                          onChange={(value) => {
                            setValues({
                              ...values,
                              visualizationType: value,
                              expirationDate:
                                value !== "EXPIRATION"
                                  ? null
                                  : values.expirationDate,
                            });
                          }}
                        >
                          <Option value="PERMANENT">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.permanent
                            }
                          </Option>
                          <Option value="CONFIRM">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.confirm
                            }
                          </Option>
                          <Option value="EXPIRATION">
                            {
                              translation.animalHandlingAlert.form
                                .visualizationType.expiration
                            }
                          </Option>
                        </Select>
                      </Row>
                    </Col>
                  </Row>

                  {/* Expiration Date — only visible when EXPIRATION */}
                  {values.visualizationType === "EXPIRATION" && (
                    <Row type="flex" style={{ marginTop: 13 }} align="middle">
                      <Col span={24}>
                        <Row className="rowLabel">
                          <label
                            className={
                              errors.expirationDate && submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {
                              translation.animalHandlingAlert.form
                                .expirationDate
                            }{" "}
                            *
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            style={{ width: "100%" }}
                            format="DD/MM/YYYY"
                            value={
                              values.expirationDate
                                ? moment(values.expirationDate)
                                : null
                            }
                            disabledDate={(current) =>
                              current && current < moment().startOf("day")
                            }
                            onChange={(date) =>
                              setValues({
                                ...values,
                                expirationDate: date ? date.toDate() : null,
                              })
                            }
                          />
                        </Row>
                      </Col>
                    </Row>
                  )}
                </div>

                <Footer>
                  <Row type="flex" justify="end">
                    <Col className="buttonsDiv">
                      <ButtonStandard
                        type="button"
                        buttonType="type7"
                        onClick={handleClose}
                      >
                        {translation.buttons.cancel}
                      </ButtonStandard>
                      <ButtonStandard type="submit" buttonType="type6">
                        {translation.buttons.save}
                      </ButtonStandard>
                    </Col>
                  </Row>
                </Footer>
              </form>
            </Spin>
          );
        }}
      </Formik>
    </Container>
  );
};

export default AnimalHandlingAlertForm;
